import type { Ancestry, AdventurerKit, MagicDiscipline, Stats, Region, Difficulty, AdventureTheme, GameLanguage } from '@/lib/db/schema';

// ============================================================================
// QUEST TYPES
// ============================================================================

export interface QuestObjective {
  id: string;
  label: string;
  flag: string; // triggerFlag that marks this objective complete
}

export interface Quest {
  id: string;
  name: string;
  description: string;
  objectives: QuestObjective[];
}

// ============================================================================
// TEMPLATE TYPES
// ============================================================================

export interface CharacterArchetype {
  id: string;
  classLabel: string;
  name: string;
  flavor: string;
  kit: AdventurerKit;
  race: Ancestry;
  magic: MagicDiscipline;
  stats: Stats;
  equipment: string[];
}

export interface GameTemplate {
  id: string;
  name: string;
  description: string;
  icon: string; // lucide-react icon name
  settings: {
    language: GameLanguage;
    difficulty: Difficulty;
    region: Region;
    adventureTheme: AdventureTheme;
    maxPlayers: number;
  };
  archetypes: CharacterArchetype[];
  dungeonMapId?: string; // references a JSON fixture in dungeons/
  quest?: Quest;
}

export const GAME_TEMPLATES: GameTemplate[] = [
  {
    id: 'dungeon_crawler',
    name: 'Dungeon Crawler',
    dungeonMapId: 'dungeon-crawler',
    description: 'Descend into the ancient ruins beneath Vannar. Recover artifacts, face traps and abominations in the crushing dark.',
    icon: 'Pickaxe',
    settings: {
      language: 'en',
      difficulty: 'classic',
      region: 'roots',
      adventureTheme: 'dungeon_crawling',
      maxPlayers: 1,
    },
    quest: {
      id: 'heartstone',
      name: 'The Heartstone of Aelindra',
      description: 'Recover the Heartstone — an ancient elven artifact of immense power — from the deepest chamber of the ruins beneath Vannar.',
      objectives: [
        { id: 'obj_sanctum', label: 'Enter the Inner Sanctum', flag: 'sanctum_entered' },
        { id: 'obj_ward', label: 'Break the Binding Ward', flag: 'ward_broken' },
        { id: 'obj_heartstone', label: 'Claim the Heartstone', flag: 'heartstone_claimed' },
      ],
    },
    archetypes: [
      {
        id: 'warrior',
        classLabel: 'Warrior',
        name: 'Kael Ironhand',
        flavor: 'A seasoned sellsword who has traded steel for coin across every layer of Vannar. The ruins call to those who have nothing left to lose.',
        kit: 'blade',
        race: 'human_pure',
        magic: 'none',
        stats: { brawn: 2, grace: 0, wits: 1, spirit: 1 },
        equipment: ['Longsword', 'Shield', 'Chain Shirt', 'Torch'],
      },
      {
        id: 'rogue',
        classLabel: 'Rogue',
        name: 'Sienna Nightveil',
        flavor: 'A Roots-born halfling who learned that locked doors are just invitations. The Deep holds treasures that no fence can resist.',
        kit: 'shadow',
        race: 'halfling_pure',
        magic: 'none',
        stats: { brawn: 0, grace: 2, wits: 1, spirit: 1 },
        equipment: ['Twin Daggers', 'Lockpicks', 'Rope', 'Dark Cloak'],
      },
      {
        id: 'mage',
        classLabel: 'Mage',
        name: 'Torrin Ashveil',
        flavor: 'Born with elven blood and a hunger for forbidden knowledge. The pre-Ebb ruins whisper secrets that no grimoire contains.',
        kit: 'sage',
        race: 'human_elven',
        magic: 'ember',
        stats: { brawn: 0, grace: 1, wits: 2, spirit: 1 },
        equipment: ['Oak Staff', 'Grimoire', 'Component Pouch', 'Lantern'],
      },
    ],
  },
];
